import { parseFontStyle, parseFontWeight, parseFontStretch } from '@adflow/utils';

export type FontVariant = {
  weight: number;
  style: string; // 'normal' | 'italic' | 'oblique'
  stretch: string; // 'normal' | 'condensed' | 'expanded' etc.
  variant?: string; // Custom variant name from DB (e.g. "Roboto SemiBoldItalic")
  label: string; // e.g. "Bold Italic"
};

export type FontStretchGroup = {
  stretch: string;
  stretchLabel: string;
  variants: FontVariant[];
};

export type FontGroup = {
  family: string;
  stretchGroups: FontStretchGroup[];
};

// Helper to extract stretch from family name and return normalized family
export const extractStretchFromFamily = (
  familyName: string,
): { baseFamily: string; stretch: string } => {
  const stretch = parseFontStretch(familyName);

  if (stretch === 'normal') {
    return { baseFamily: familyName, stretch: 'normal' };
  }

  // Remove stretch keywords from family name
  const stretchKeywords = [
    'ultra condensed',
    'ultracondensed',
    'extra condensed',
    'extracondensed',
    'semi condensed',
    'semicondensed',
    'condensed',
    'narrow',
    'compressed',
    'semi expanded',
    'semiexpanded',
    'extra expanded',
    'extraexpanded',
    'ultra expanded',
    'ultraexpanded',
    'expanded',
    'wide',
  ];

  let baseFamily = familyName;
  const lowerFamily = familyName.toLowerCase();

  for (const keyword of stretchKeywords) {
    const regex = new RegExp(`\\s*${keyword}\\s*`, 'gi');
    if (lowerFamily.includes(keyword)) {
      baseFamily = baseFamily.replace(regex, ' ').trim();
      break;
    }
  }

  return { baseFamily, stretch };
};

// Helper to generate stretch label
export const getStretchLabel = (stretch: string): string => {
  const stretchMap: Record<string, string> = {
    'ultra-condensed': 'Ultra Condensed',
    'extra-condensed': 'Extra Condensed',
    'semi-condensed': 'Semi Condensed',
    condensed: 'Condensed',
    normal: 'Normal',
    'semi-expanded': 'Semi Expanded',
    expanded: 'Expanded',
    'extra-expanded': 'Extra Expanded',
    'ultra-expanded': 'Ultra Expanded',
  };
  return stretchMap[stretch] || stretch;
};

export const normalizeVariantLabel = (label?: string | null): string => {
  if (!label) return '';
  return label.trim().toLowerCase();
};

// Helper to generate technical hint for custom variant names
export const getVariantHint = (weight: number, style: string): string => {
  return getGeneratedVariantLabel(weight, style);
};

const getGeneratedVariantLabel = (weight: number, style: string): string => {
  const weightMap: Record<number, string> = {
    100: 'Thin',
    200: 'Extra Light',
    300: 'Light',
    400: 'Regular',
    500: 'Medium',
    600: 'Semi Bold',
    700: 'Bold',
    800: 'Extra Bold',
    900: 'Black',
    950: 'Extra Black',
  };

  // If exact match exists, use it
  let weightLabel = weightMap[weight];

  // If no exact match, find the closest weight
  if (!weightLabel) {
    const weights = Object.keys(weightMap)
      .map(Number)
      .sort((a, b) => a - b);
    const closest = weights.reduce((prev, curr) =>
      Math.abs(curr - weight) < Math.abs(prev - weight) ? curr : prev,
    );
    weightLabel = weightMap[closest];
  }

  const styleLabel = style === 'italic' ? 'Italic' : style === 'oblique' ? 'Oblique' : '';

  if (weight === 400 && style === 'normal') return 'Regular';
  if (style === 'normal') return weightLabel;
  if (weight === 400) return styleLabel; // e.g. "Italic" instead of "Regular Italic"

  return `${weightLabel} ${styleLabel}`;
};

// Helper to generate label - prefers custom variant name if provided
export const getVariantLabel = (weight = 400, style = 'normal', customVariant?: string): string => {
  if (customVariant) {
    return customVariant;
  }
  return getGeneratedVariantLabel(weight, style);
};

type FontSource = 'custom' | 'standard' | 'document';

interface FontAggregationContext {
  familyMap: Map<string, Map<string, FontVariant[]>>;
  customFamilies: Set<string>;
  standardFamilies: Set<string>;
}

function addFontVariant(
  context: FontAggregationContext,
  familyName: string,
  weight: number,
  style: string,
  variantStretch: string,
  customVariant: string | undefined,
  source: FontSource,
): void {
  // For custom fonts, use the family name as-is without normalization
  // For other sources, extract stretch from family name if present
  const shouldNormalizeFamily = source !== 'custom';
  const { baseFamily, stretch: familyStretch } = shouldNormalizeFamily
    ? extractStretchFromFamily(familyName)
    : { baseFamily: familyName, stretch: 'normal' };

  // Skip if this is a document font trying to add to a custom or standard family
  if (source === 'document') {
    // Check both normalized and exact family names for custom fonts
    const hasExactMatch = context.customFamilies.has(familyName);
    const hasNormalizedMatch = context.customFamilies.has(baseFamily);
    if (hasExactMatch || hasNormalizedMatch || context.standardFamilies.has(baseFamily)) {
      return; // Don't mix document fonts with custom or standard families
    }
  }

  // Priority: Use font's actual stretch value first, only fall back to family name extraction
  const finalStretch = variantStretch !== 'normal' ? variantStretch : familyStretch;

  let stretchMap = context.familyMap.get(baseFamily);
  if (!stretchMap) {
    stretchMap = new Map();
    context.familyMap.set(baseFamily, stretchMap);
  }

  let variants = stretchMap.get(finalStretch);
  if (!variants) {
    variants = [];
    stretchMap.set(finalStretch, variants);
  }

  const exists = variants.some((v) => v.weight === weight && v.style === style);
  if (!exists) {
    variants.push({
      weight,
      style,
      stretch: finalStretch,
      variant: customVariant,
      label: getVariantLabel(weight, style, customVariant),
    });
  }
}

class FontAggregator {
  private context: FontAggregationContext;

  constructor() {
    this.context = {
      familyMap: new Map(),
      customFamilies: new Set(),
      standardFamilies: new Set(),
    };
  }

  addCustomFonts(
    fonts: {
      fontFamily: string;
      weight?: number;
      style?: string;
      stretch?: string;
      variant?: string;
    }[],
  ): this {
    // Track custom family names - use exact names as uploaded, don't normalize
    fonts.forEach((f) => {
      // For custom fonts, preserve the exact family name without stretch extraction
      this.context.customFamilies.add(f.fontFamily);
    });

    // Add custom font variants
    fonts.forEach((f) => {
      const weight = f.weight || 400;
      const style = f.style || 'normal';
      const stretch = f.stretch || 'normal';
      const variant = f.variant;
      addFontVariant(this.context, f.fontFamily, weight, style, stretch, variant, 'custom');
    });

    return this;
  }

  addStandardFonts(fonts: string[]): this {
    // Track standard family names
    fonts.forEach((family) => this.context.standardFamilies.add(family));

    // Add standard font variants (just the default)
    fonts.forEach((family) => {
      addFontVariant(this.context, family, 400, 'normal', 'normal', undefined, 'standard');
    });

    return this;
  }

  addDocumentFonts(): this {
    // Add fonts from document.fonts API - only if not already in custom or standard
    if (typeof document !== 'undefined' && document.fonts) {
      document.fonts.forEach((fontFace) => {
        const weight = parseFontWeight(fontFace.weight);
        const style = parseFontStyle(fontFace.style);
        const stretch = parseFontStretch(fontFace.stretch || 'normal');
        addFontVariant(
          this.context,
          fontFace.family,
          weight,
          style,
          stretch,
          undefined,
          'document',
        );
      });
    }

    return this;
  }

  addOriginalFont(original: {
    family?: string | null;
    style?: string | null;
    weight?: number | null;
    stretch?: string | null;
  }): this {
    if (!original.family) return this;

    const weight = original.weight || 400;
    const style = original.style || 'normal';
    const stretch = original.stretch || 'normal';

    // Determine source based on tracked families
    // Check exact match first for custom fonts (they preserve exact names)
    let source: FontSource = 'document';
    if (this.context.customFamilies.has(original.family)) {
      source = 'custom';
    } else if (this.context.standardFamilies.has(original.family)) {
      source = 'standard';
    } else {
      // Check normalized name as fallback for document fonts
      const { baseFamily } = extractStretchFromFamily(original.family);
      if (this.context.customFamilies.has(baseFamily)) {
        source = 'custom';
      } else if (this.context.standardFamilies.has(baseFamily)) {
        source = 'standard';
      }
    }

    addFontVariant(this.context, original.family, weight, style, stretch, undefined, source);

    return this;
  }

  build(): FontGroup[] {
    return this.sortAndGroup();
  }

  private sortAndGroup(): FontGroup[] {
    // Turn Map -> array with stretch grouping
    const grouped: FontGroup[] = Array.from(this.context.familyMap.entries()).map(
      ([family, stretchMap]) => {
        const stretchGroups: FontStretchGroup[] = Array.from(stretchMap.entries()).map(
          ([stretch, variants]) => {
            // Sort variants by weight, then style
            const sortedVariants = variants.sort((a, b) => {
              if (a.weight !== b.weight) return a.weight - b.weight;
              return a.style.localeCompare(b.style);
            });

            return {
              stretch,
              stretchLabel: getStretchLabel(stretch),
              variants: sortedVariants,
            };
          },
        );

        // Sort stretch groups: normal first, then alphabetically
        stretchGroups.sort((a, b) => {
          if (a.stretch === 'normal') return -1;
          if (b.stretch === 'normal') return 1;
          return a.stretch.localeCompare(b.stretch);
        });

        return {
          family,
          stretchGroups,
        };
      },
    );

    return grouped.sort((a, b) => a.family.localeCompare(b.family));
  }
}

export function buildAvailableFonts({
  customFonts,
  standardFonts,
  originalFamily,
  originalStyle,
  originalWeight,
  originalStretch,
}: {
  customFonts: {
    fontFamily: string;
    weight?: number;
    style?: string;
    stretch?: string;
    variant?: string;
  }[];
  standardFonts: string[];
  originalFamily?: string | null;
  originalStyle?: string | null;
  originalWeight?: number | null;
  originalStretch?: string | null;
}): FontGroup[] {
  return new FontAggregator()
    .addCustomFonts(customFonts)
    .addStandardFonts(standardFonts)
    .addDocumentFonts()
    .addOriginalFont({
      family: originalFamily,
      style: originalStyle,
      weight: originalWeight,
      stretch: originalStretch,
    })
    .build();
}
